import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface BatchSummary {
  id: string;
  name: string;
  description: string | null;
  sourceType: string; // 'SharePoint' | 'Mailbox' | 'Github' | 'Upload' | 'Connector'
  /** Concrete origin: SharePoint, GoogleDrive, Slack, Teams, Ftp, Upload, Mailbox, Github. */
  sourceProvider?: string | null;
  driveId: string | null;
  folderId: string | null;
  status: string;
  totalDocuments: number;
  processedDocuments: number;
  failedDocuments: number;
  percentComplete: number;
  createdAt: string;
  completedAt: string | null;
  startedAt?: string | null;
}

export interface BatchListData {
  total: number;
  page: number;
  pageSize: number;
  items: BatchSummary[];
}

export interface BatchListResponse {
  statusCode: number;
  success: boolean;
  message: string;
  data: BatchListData;
}

export interface DocumentItem {
  id: string;
  batchId: string;
  sharePointItemId: string;
  fileName: string;
  filePath: string;
  fileExtension: string;
  fileSizeInBytes: number;
  blobUri: string | null;
  status: string;
  errorMessage: string | null;
  retryCount: number;
  createdAt: string;
  processedAt: string | null;
  classification: string | null;
  subClassification: string | null;
  connector?: string;
  stageTimingsJson?: string | null;
  /** Version control outcome: new | update | ignore. */
  versionAction?: string | null;
  /** Version control % stats as stored (raw JSON). */
  versionStatsJson?: string | null;
}

export interface BatchDetail extends BatchSummary {
  documents: DocumentItem[];
}

export interface BatchDetailResponse {
  statusCode: number;
  success: boolean;
  message: string;
  data: {
    batch: BatchDetail;
    totalDocuments: number;
    page: number;
    pageSize: number;
  };
}

export interface CreateBatchPayload {
  name: string;
  description?: string;
  driveId?: string | null;
  folderId?: string | null;
  itemIds?: string[];
  /** Set for Google Drive / FTP batches; driveId is then that connector's root id. */
  connectorId?: string | null;
}

export interface CreateBatchResponse {
  statusCode: number;
  success: boolean;
  message: string;
  data: {
    batchId: string;
    name: string;
    description: string | null;
    status: string;
  };
}

export interface StartIngestionResponse {
  statusCode: number;
  success: boolean;
  message: string;
  data: {
    batchId: string;
    name: string;
    description: string | null;
    status: string;
  };
}

export interface AddBatchDocumentsPayload {
  driveId: string;
  folderId?: string | null;
  itemIds?: string[];
  connectorId?: string | null;
}

export interface AddBatchDocumentsResponse {
  statusCode: number;
  success: boolean;
  message: string;
  data: {
    batchId: string;
    addedCount: number;
    totalDocuments: number;
  };
}

export interface ChunkRoleInfo {
  role: string;
  confidence: number;
  evidence: string[];
}

export interface ChunkWithRoles {
  chunkId: string;
  heading: string;
  roles: ChunkRoleInfo[];
}

export interface ChunkRolesResponse {
  documentId: string;
  chunks: ChunkWithRoles[];
}

@Injectable({
  providedIn: 'root'
})
export class Batch {
  private baseUrl = `${environment.apiUrl}/batches`;

  constructor(private http: HttpClient) {}

  getBatches(status?: string, page: number = 1, pageSize: number = 20): Observable<BatchListResponse> {
    let params = `?page=${page}&pageSize=${pageSize}`;
    if (status) params += `&status=${status}`;
    return this.http.get<BatchListResponse>(`${this.baseUrl}${params}`);
  }

  getBatchById(id: string, page: number = 1, pageSize: number = 50): Observable<BatchDetailResponse> {
    return this.http.get<BatchDetailResponse>(`${this.baseUrl}/${id}?page=${page}&pageSize=${pageSize}`);
  }

  createBatch(payload: CreateBatchPayload): Observable<CreateBatchResponse> {
    return this.http.post<CreateBatchResponse>(this.baseUrl, {
      Name: payload.name,
      Description: payload.description ?? null,
      DriveId: payload.driveId ?? null,
      FolderId: payload.folderId ?? null,
      ItemIds: payload.itemIds ?? null,
      ConnectorId: payload.connectorId ?? null
    });
  }

  /** Creates a draft batch directly from files picked on the user's own computer (up to 1GB total). */
  uploadBatch(name: string, description: string | undefined, files: File[]): Observable<CreateBatchResponse> {
    const form = new FormData();
    form.append('name', name);
    if (description) form.append('description', description);
    for (const file of files) {
      form.append('files', file, file.name);
      // When the file was last changed on this computer, in the same order as files: version
      // control uses it to tell which of two conflicting documents is newer.
      form.append('lastModified', String(file.lastModified ?? 0));
    }
    return this.http.post<CreateBatchResponse>(`${this.baseUrl}/upload`, form);
  }

  startIngestion(batchId: string): Observable<StartIngestionResponse> {
    return this.http.post<StartIngestionResponse>(`${this.baseUrl}/${batchId}/start-ingestion`, {});
  }

  addDocumentsToBatch(batchId: string, payload: AddBatchDocumentsPayload): Observable<AddBatchDocumentsResponse> {
    return this.http.post<AddBatchDocumentsResponse>(`${this.baseUrl}/${batchId}/documents`, {
      DriveId: payload.driveId,
      FolderId: payload.folderId ?? null,
      ItemIds: payload.itemIds ?? null,
      ConnectorId: payload.connectorId ?? null
    });
  }

  deleteBatch(batchId: string): Observable<any> {
    return this.http.delete(`${this.baseUrl}/${batchId}`);
  }

  deleteDocument(batchId: string, documentId: string): Observable<any> {
    return this.http.delete(`${this.baseUrl}/${batchId}/documents/${documentId}`);
  }

  getDocumentChunksRoles(documentId: string): Observable<ChunkRolesResponse> {
    return this.http.get<ChunkRolesResponse>(`${environment.apiUrl}/documents/${documentId}/chunks/roles`);
  }

  getDocumentContent(documentId: string): Observable<DocumentContentResponse> {
    return this.http.get<DocumentContentResponse>(`${environment.apiUrl}/documents/${documentId}/content`);
  }

  getDocumentFile(documentId: string): Observable<Blob> {
    return this.http.get(`${environment.apiUrl}/documents/${documentId}/file`, { responseType: 'blob' });
  }
}

export interface DocumentContent {
  fileName: string;
  kind: 'text' | 'binary' | 'unavailable';
  text: string | null;
  mediaType: string | null;
  message: string | null;
}

export interface DocumentContentResponse {
  statusCode: number;
  success: boolean;
  message: string;
  data: DocumentContent;
}